<template>
    <div class="w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto">
            <div class="p-8 space-y-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">Workshop Management</span>
                </nav>

                <!-- Header -->
                <div class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 mb-6 sm:mb-8">
                    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div class="min-w-0">
                            <h1 class="text-3xl font-bold text-blue-900 flex items-center gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                    class="h-8 w-8">
                                    <path
                                        d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a.75.75 0 01-.231 1.337 49.949 49.949 0 00-9.902 3.912l-.003.002c-.114.06-.227.119-.34.18a.75.75 0 01-.707 0A50 50 0 000 13.713a.75.75 0 01-.231-1.337A60.653 60.653 0 0111.7 2.805z" />
                                    <path
                                        d="M13.06 15.473a48.45 48.45 0 017.666-3.282c.134 1.414.22 2.843.255 4.285a.75.75 0 01-.46.711 47.878 47.878 0 00-8.105 4.342.75.75 0 01-.832 0 47.877 47.877 0 00-8.104-4.342.75.75 0 01-.461-.71c.035-1.442.121-2.87.255-4.286A48.4 48.4 0 016 13.18v1.27a1.5 1.5 0 00-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.661a6.729 6.729 0 00.551-1.608 1.5 1.5 0 00.14-2.67l-.41-.21a48.415 48.415 0 013.36-.494zm1.12 1.653a6 6 0 01.659-1.234 48.44 48.44 0 017.666-3.282c.152.315.266.642.35.977a.75.75 0 01-.45 1.083 48.35 48.35 0 00-8.048 4.304.75.75 0 01-.833 0 48.41 48.41 0 00-7.87-3.108.75.75 0 01-.35-.977c.084-.335.198-.662.35-.977a48.442 48.442 0 017.666 3.281c-.224.219-.432.454-.628.697z" />
                                </svg>
                                Workshop Management
                            </h1>
                            <p class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
                                Empower students and alumni with essential career skills through career counselling
                                sessions, structured activities, track submissions, and manage the work readiness
                                program effectively.
                            </p>
                        </div>

                        <div class="flex items-center justify-start lg:justify-end">
                            <button v-if="hasPermission('work-readiness-program-create')" @click="openActivityModal()"
                                class="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 4v16m8-8H4"></path>
                                </svg>
                                <span>Add Activity</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Section 1: Activities -->
                <section class="bg-white rounded-2xl shadow-md p-6">
                    <div class="mb-4">
                        <h2 class="text-xl font-semibold text-indigo-600">🎯 Activities</h2>
                    </div>

                    <!-- Tabs -->
                    <div class="mb-6 border-b border-gray-200">
                        <nav class="flex space-x-8" aria-label="Tabs">
                            <button @click="switchTab('current')" :class="[
                                'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                                activeTab === 'current'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            ]">
                                Current Activities
                                <span v-if="currentActivitiesCount > 0"
                                    class="ml-2 bg-indigo-100 text-indigo-600 py-0.5 px-2 rounded-full text-xs">
                                    {{ currentActivitiesCount }}
                                </span>
                            </button>
                            <button @click="switchTab('upcoming')" :class="[
                                'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                                activeTab === 'upcoming'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            ]">
                                Upcoming Activities
                                <span v-if="upcomingActivitiesCount > 0"
                                    class="ml-2 bg-indigo-100 text-indigo-600 py-0.5 px-2 rounded-full text-xs">
                                    {{ upcomingActivitiesCount }}
                                </span>
                            </button>
                            <button @click="switchTab('past')" :class="[
                                'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                                activeTab === 'past'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            ]">
                                Past Activities
                                <span v-if="pastActivitiesCount > 0"
                                    class="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                                    {{ pastActivitiesCount }}
                                </span>
                            </button>
                            <button @click="switchTab('all')" :class="[
                                'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                                activeTab === 'all'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            ]">
                                All Activities
                                <span v-if="allActivitiesCount > 0"
                                    class="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                                    {{ allActivitiesCount }}
                                </span>
                            </button>
                        </nav>
                    </div>
                    <div v-if="$apollo.queries.work_readiness_programs.loading"
                        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="n in first" :key="n"
                            class="bg-white border border-gray-200 rounded-2xl shadow-md overflow-hidden animate-pulse">
                            <div class="bg-gradient-to-br from-gray-200 to-gray-300 h-24"></div>
                            <div class="p-5 space-y-3">
                                <div class="h-3 bg-gray-200 rounded w-full"></div>
                                <div class="h-3 bg-gray-200 rounded w-5/6"></div>
                                <div class="h-3 bg-gray-200 rounded w-4/6"></div>
                                <div class="pt-2 border-t border-gray-100">
                                    <div class="flex items-center space-x-2">
                                        <div class="w-8 h-8 bg-gray-200 rounded-full"></div>
                                        <div class="flex-1">
                                            <div class="h-2 bg-gray-200 rounded w-20 mb-2"></div>
                                            <div class="h-3 bg-gray-200 rounded w-24"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="px-5 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                                <div class="h-4 bg-gray-200 rounded w-24"></div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="!$apollo.queries.work_readiness_programs.loading && !hasItems"
                        class="flex justify-center items-center py-12">
                        <div class="text-center">
                            <p class="text-xl font-medium text-gray-500">No {{ getTabLabel() }} found!</p>
                            <p class="text-sm text-gray-400 mt-2">Try selecting a different tab.</p>
                        </div>
                    </div>
                    <div v-else class="flex flex-col">
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div v-for="(activity, index) in items" :key="index"
                                class="bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer transform hover:-translate-y-1 flex flex-col">

                                <!-- Card Header with Gradient -->
                                <div @click.stop="goToActivity(activity.token)"
                                    class="bg-gradient-to-br from-indigo-500 to-indigo-600 p-5 text-white">
                                    <div class="flex items-start justify-between mb-2">
                                        <h3 class="text-lg font-bold leading-tight pr-2">{{ activity.title }}</h3>
                                        <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-full px-3 py-1">
                                            <span class="text-sm font-semibold">+{{ activity.points }} pts</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Card Body -->
                                <div @click.stop="goToActivity(activity.token)" class="p-5 space-y-3">
                                    <p class="text-sm text-gray-600 leading-relaxed line-clamp-3 min-h-[3.75rem]">
                                        {{ activity.description }}
                                    </p>

                                    <!-- Created At -->
                                    <div v-if="activity.created_at"
                                        class="flex items-center text-xs text-gray-500 pt-2 border-t border-gray-100">
                                        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                            </path>
                                        </svg>
                                        <span class="font-medium">Created:</span>
                                        <span class="ml-1">{{ formatDate(activity.created_at) }}</span>
                                    </div>

                                    <!-- Submission Statistics -->
                                    <div class="pt-3 border-t border-gray-100">
                                        <p class="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                            Submissions</p>
                                        <div class="grid grid-cols-2 gap-2">
                                            <!-- Pending Submissions -->
                                            <div
                                                class="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-2.5 border border-yellow-200">
                                                <div class="flex items-center justify-between">
                                                    <div class="flex items-center space-x-2">
                                                        <div class="w-2 h-2 bg-yellow-400 rounded-full animate-pulse">
                                                        </div>
                                                        <span class="text-xs font-medium text-yellow-700">Pending</span>
                                                    </div>
                                                    <span class="text-base font-bold text-yellow-700">{{
                                                        getPendingSubmissions(activity) }}</span>
                                                </div>
                                            </div>

                                            <!-- Approved Submissions -->
                                            <div
                                                class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-2.5 border border-green-200">
                                                <div class="flex items-center justify-between">
                                                    <div class="flex items-center space-x-2">
                                                        <svg class="w-3.5 h-3.5 text-green-600" fill="none"
                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z">
                                                            </path>
                                                        </svg>
                                                        <span class="text-xs font-medium text-green-700">Approved</span>
                                                    </div>
                                                    <span class="text-base font-bold text-green-700">{{
                                                        getApprovedSubmissions(activity) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Card Footer with Actions -->
                                <div class="flex-auto flex flex-col justify-end">
                                    <!-- Creator Info -->
                                    <div class="px-5 py-2">
                                        <div v-if="activity.creator && activity.creator.name"
                                            class="flex items-center pt-2 border-t border-gray-100">
                                            <div class="flex items-center space-x-2">
                                                <div class="relative flex-shrink-0">
                                                    <img :src="getProfilePicture(activity.creator.profile_picture_url)"
                                                        :alt="activity.creator.name"
                                                        class="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                                                        @error="handleImageError" />
                                                </div>
                                                <div>
                                                    <p class="text-xs text-gray-400 font-medium">Created by</p>
                                                    <p class="text-sm text-gray-700 font-semibold">{{
                                                        activity.creator.name
                                                    }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="px-5 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                                        <div class="flex items-center justify-between">
                                            <button @click.stop="goToActivity(activity.token)"
                                                class="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center space-x-1 transition-colors">
                                                <span>View Details</span>
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2" d="M9 5l7 7-7 7"></path>
                                                </svg>
                                            </button>

                                            <div class="flex items-center space-x-2">
                                                <button
                                                    v-if="canEditActivity(activity)"
                                                    @click.stop="openActivityModal(activity)"
                                                    class="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                    title="Edit Activity">
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                                                        </path>
                                                    </svg>
                                                </button>
                                                <button
                                                    v-if="hasPermission('work-readiness-program-delete') && isTheCreator(activity.creator.id) && isUpcomingActivity(activity)"
                                                    @click.stop="deleteActivity(activity.token)"
                                                    class="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Activity">
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                                                        </path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="mt-4">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                        </div>
                    </div>
                </section>

                <!-- Section 2: Badge System & Levels -->
                <section class="bg-white rounded-2xl shadow-md p-6">
                    <div class="flex justify-between items-center mb-4">
                        <h2 class="text-xl font-semibold text-yellow-500">🏅 Badge System & Levels</h2>
                        <button v-if="hasPermission('badge-create') && false" @click="openBadgeModal()"
                            class="text-sm bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600">+
                            Add</button>
                    </div>
                    <div v-if="badgesLoading" class="grid grid-cols-1 md:grid-cols-5 gap-6">
                        <div v-for="n in 5" :key="n"
                            class="animate-pulse space-y-4 p-4 border rounded-xl bg-yellow-50 shadow max-w-xs mx-auto">
                            <div class="bg-yellow-300 rounded-full h-12 w-12 mx-auto mb-2"></div>
                            <div class="h-6 bg-yellow-300 rounded mx-auto w-3/4"></div>
                            <div class="h-4 bg-yellow-300 rounded mx-auto w-1/2"></div>
                            <div class="mt-4 flex justify-center space-x-2">
                                <div class="h-6 w-16 bg-yellow-300 rounded"></div>
                                <div class="h-6 w-16 bg-yellow-300 rounded"></div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="badgeError" class="text-center text-xl font-medium text-red-500">{{ error }}</div>
                    <div v-else class="grid grid-cols-1 md:grid-cols-5 gap-6">
                        <div v-for="(badge, index) in badges" :key="index"
                            class="text-center p-4 border rounded-xl bg-yellow-50 shadow hover:scale-105 transition">
                            <div class="text-4xl mb-2">{{ badge.emoji }}</div>
                            <h3 class="text-lg font-semibold text-gray-700">{{ badge.name }} {{ badge.title }}</h3>
                            <p class="text-sm text-gray-500">{{ badge.points_range }}</p>
                            <div class="mt-4 flex justify-center space-x-2">
                                <button v-if="hasPermission('badge-edit') && false"
                                    @click="openBadgeModal(badge, index)" class="text-yellow-700 text-sm">Edit</button>
                                <button v-if="hasPermission('badge-delete') && false" @click="deleteBadge(index)"
                                    class="text-red-500 text-sm">Delete</button>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section 3: Leaderboard -->
                <section class="bg-white rounded-2xl shadow-md p-6">
                    <h2 class="text-xl font-semibold text-blue-600 mb-4">🏆 Leaderboard</h2>
                    <div class="overflow-x-auto">
                        <table class="min-w-full border border-gray-200 text-sm text-left">
                            <thead class="bg-gray-100 text-gray-700">
                                <tr>
                                    <th class="p-3">#</th>
                                    <th class="p-3">Rank</th>
                                    <th class="p-3">Student Name</th>
                                    <th class="p-3">Points</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="leaderboardLoading">
                                    <tr v-for="n in 3" :key="n">
                                        <td colspan="3" class="py-2">
                                            <div class="h-4 w-full rounded animate-shimmer"></div>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr v-for="(item, index) in leaderboard" :key="index"
                                        class="border-t hover:bg-gray-50">
                                        <td class="p-3 font-bold">{{ leaderboardPaginatorInfo.firstItem
                                            + index }}</td>
                                        <td class="p-3 font-bold text-indigo-600">#{{ item.rank }}</td>
                                        <td @click.stop="viewStudentProfile(item.user.students.student_id)"
                                            class="p-3 hover:text-blue-600 transition-colors cursor-pointer">{{
                                                item.user.name }}</td>
                                        <td class="p-3 text-green-600">{{ item.points }}</td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="leaderboard.length" class="mt-4">
                        <pagination :paginatorInfo="leaderboardPaginatorInfo" :setPage="setLeaderboardPage">
                        </pagination>
                    </div>
                </section>
            </div>
        </div>

        <!-- Activity Modal -->
        <base-modal v-if="showActivityModal" :title="getActivityModalTitle" @close="resetActivityModal">
            <div class="px-8 pb-4">
                <form class="space-y-5" @submit.prevent="saveActivity">
                    <!-- Title -->
                    <div class="mt-4">
                        <label for="activity-title" class="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                        <input id="activity-title" v-model="activityForm.title.val" type="text"
                            class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            :class="activityForm.title.isValid ? 'border-gray-300' : 'border-red-500'"
                            placeholder="Enter activity title" />
                        <transition name="fade">
                            <p v-if="!activityForm.title.isValid" class="text-red-500 text-sm mt-1">
                                {{ activityForm.title.msg }}
                            </p>
                        </transition>
                    </div>

                    <!-- Description -->
                    <div>
                        <label for="activity-description"
                            class="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                        <textarea id="activity-description" v-model="activityForm.description.val" rows="4"
                            class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                            :class="activityForm.description.isValid ? 'border-gray-300' : 'border-red-500'"
                            placeholder="Enter activity description"></textarea>
                        <transition name="fade">
                            <p v-if="!activityForm.description.isValid" class="text-red-500 text-sm mt-1">
                                {{ activityForm.description.msg }}
                            </p>
                        </transition>
                    </div>

                    <!-- Registration Type and External Link (same line) -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Registration Type</label>
                            <div class="flex gap-6">
                                <label class="flex flex-col cursor-pointer">
                                    <span class="flex items-center">
                                        <input type="radio" v-model="activityForm.is_external.val" :value="false"
                                            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                                        <span class="ml-2 text-sm text-gray-700">Internal</span>
                                    </span>
                                    <span class="text-xs text-gray-500 mt-0.5 ml-6">(Submit through portal)</span>
                                </label>
                                <label class="flex flex-col cursor-pointer">
                                    <span class="flex items-center">
                                        <input type="radio" v-model="activityForm.is_external.val" :value="true"
                                            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                                        <span class="ml-2 text-sm text-gray-700">External</span>
                                    </span>
                                    <span class="text-xs text-gray-500 mt-0.5 ml-6">(Registration via external
                                        link)</span>
                                </label>
                            </div>
                        </div>
                        <div v-if="activityForm.is_external.val">
                            <label for="activity-external-link"
                                class="block text-sm font-medium text-gray-700 mb-1.5">External
                                Registration Link</label>
                            <input id="activity-external-link" v-model="activityForm.external_link.val" type="url"
                                class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                :class="activityForm.external_link.isValid ? 'border-gray-300' : 'border-red-500'"
                                placeholder="https://..." />
                            <transition name="fade">
                                <p v-if="!activityForm.external_link.isValid" class="text-red-500 text-sm mt-1">
                                    {{ activityForm.external_link.msg }}
                                </p>
                            </transition>
                        </div>
                    </div>

                    <!-- Valuation Type and Points (same line) -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1.5">Valuation Type</label>
                            <div class="flex gap-4">
                                <label class="flex items-center cursor-pointer">
                                    <input type="radio" v-model="activityForm.valuation_type.val" value="manual"
                                        class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                                    <span class="ml-2 text-sm text-gray-700">Manual</span>
                                </label>
                                <label class="flex items-center cursor-pointer">
                                    <input type="radio" v-model="activityForm.valuation_type.val" value="auto"
                                        class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                                    <span class="ml-2 text-sm text-gray-700">Auto</span>
                                </label>
                            </div>
                        </div>
                        <div>
                            <label for="activity-points"
                                class="block text-sm font-medium text-gray-700 mb-1.5">Points</label>
                            <input id="activity-points" type="number" min="1" v-model.number="activityForm.points.val"
                                class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                :class="activityForm.points.isValid ? 'border-gray-300' : 'border-red-500'"
                                placeholder="e.g. 30" />
                            <transition name="fade">
                                <p v-if="!activityForm.points.isValid" class="text-red-500 text-sm mt-1">
                                    {{ activityForm.points.msg }}
                                </p>
                            </transition>
                        </div>
                    </div>

                    <!-- Date Range -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1.5">Start Date</label>
                            <date-picker v-model:value="activityForm.start_date.val" format="YYYY-MM-DD HH:mm:ss"
                                type="datetime" value-type="format" placeholder="Select start date"
                                class="activity-date-picker"
                                :popup-style="{ zIndex: 10060 }"></date-picker>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1.5">End Date</label>
                            <date-picker v-model:value="activityForm.end_date.val" format="YYYY-MM-DD HH:mm:ss"
                                type="datetime" value-type="format" placeholder="Select end date"
                                class="activity-date-picker"
                                :popup-style="{ zIndex: 10060 }"></date-picker>
                        </div>
                    </div>

                    <!-- Buttons -->
                    <div class="flex justify-end gap-3 pt-5 border-t border-gray-200">
                        <button type="button" @click="resetActivityModal"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                            Cancel
                        </button>
                        <button type="submit"
                            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </base-modal>

        <!-- Badge Modal -->
        <div v-if="showBadgeModal" class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div class="bg-white p-6 rounded-xl w-full max-w-md space-y-4 shadow-lg">
                <h3 class="text-lg font-semibold text-yellow-700">{{ getBadgeModalTitle }}
                </h3>
                <input v-model="badgeForm.name" placeholder="Badge Name" class="w-full p-2 border rounded" />
                <input type="number" v-model.number="badgeForm.level" placeholder="Level"
                    class="w-full p-2 border rounded" />
                <input v-model="badgeForm.emoji" placeholder="Emoji (🏅, 🥈...)" class="w-full p-2 border rounded" />
                <div class="flex justify-end space-x-2">
                    <button @click="saveBadge"
                        class="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600">Save</button>
                    <button @click="resetBadgeModal" class="text-gray-500 px-4 py-2">Cancel</button>
                </div>
            </div>
        </div>

        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

        <transition name="fade-student" mode="out-in">
            <base-full-modal v-if="showStudentProfileFullModal" title="Student Profile" @close="closeFullModal">
                <student-profile :student-id="studentId"></student-profile>
            </base-full-modal>
        </transition>

    </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from "../../components/shared/pagination.vue";
import StudentProfile from "../student-profile/index.vue";

const CREATE_WORK_READINESS_PROGRAM = gql`
    mutation CreateWorkReadinessProgram($input: CreateWorkReadinessProgramInput!) {
    createWorkReadinessProgram(input: $input) {
            error
            message
        }
    }
`;

const EDIT_WORK_READINESS_PROGRAM = gql`
    mutation EditWorkReadinessProgram($token: String!, $input: CreateWorkReadinessProgramInput!) {
        editWorkReadinessProgram(token: $token, input: $input) {
            error
            message
        }
    }
`;

const DELETE_WORK_READINESS_PROGRAM = gql`
    mutation DeleteWorkReadinessProgram($token: String!) {
        deleteWorkReadinessProgram(token: $token) {
            error
            message
        }
    }
`;

const GET_BADGES = gql`
  query GetBadges {
    badges {
      id
      name
      title
      points_min
      points_max
      emoji
      image_url
      points_range
    }
  }
`;

const GET_LEADERBOARD = gql`
  query GetLeaderboard($page: Int!, $first: Int!) {
    leaderboardIndex(page: $page, first: $first) {
      error
      error_code
      message
      data {
        points
        rank
        user {
          name
          students {
            student_id
          }
        }
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export default {
    name: "work-readiness-program",
    components: {
        Pagination,
        StudentProfile,
    },
    data() {
        return {
            work_readiness_programs: [],
            first: 6,
            page: 1,
            isLoading: false,
            showActivityModal: false,
            activityForm: {
                title: {
                    val: "",
                    isValid: true,
                    msg: "",
                },
                description: {
                    val: "",
                    isValid: true,
                    msg: "",
                },
                points: {
                    val: 0,
                    isValid: true,
                    msg: "",
                },
                valuation_type: {
                    val: "manual",
                    isValid: true,
                    msg: "",
                },
                is_external: {
                    val: false,
                    isValid: true,
                    msg: "",
                },
                external_link: {
                    val: "",
                    isValid: true,
                    msg: "",
                },
                start_date: {
                    val: null,
                    isValid: true,
                    msg: "",
                },
                end_date: {
                    val: null,
                    isValid: true,
                    msg: "",
                },
            },
            activityFormIndex: null,
            activityFormToken: null,
            badges: [],
            badgesLoading: false,
            badgeError: null,
            showBadgeModal: false,
            badgeForm: { name: '', level: 1, emoji: '🏅' },
            badgeFormIndex: null,
            leaderboard: [],
            leaderboardPageNumber: 1,
            leaderboardRecordPerPage: 10,
            leaderboardLoading: false,
            leaderboardError: null,
            leaderboardPaginatorInfo: null,
            studentId: null,
            showStudentProfileFullModal: false,
            activeTab: 'current', // 'current', 'upcoming', 'past', 'all'
            isOpeningActivityModal: false,
        };
    },
    watch: {
        'activityForm.is_external.val'(isExternal) {
            if (this.isOpeningActivityModal) return;
            this.activityForm.valuation_type.val = isExternal ? 'auto' : 'manual';
        },
        work_readiness_programs(newVal) {
            if (newVal.error) {
                if (newVal.error_code == 403) {
                    this.$router.replace('/no-permission');
                } else if (newVal.error_code == 401) {
                    this.$router.replace('/');
                } else {
                    alert(newVal.message);
                    this.$router.replace('/');
                }
            }
        },
    },
    computed: {
        items() {
            return this.work_readiness_programs.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        allActivitiesCount() {
            if (this.activeTab === 'all' && this.paginatorInfo) {
                return this.paginatorInfo.total || 0;
            }
            return 0;
        },
        currentActivitiesCount() {
            if (this.activeTab === 'current' && this.paginatorInfo) {
                return this.paginatorInfo.total || 0;
            }
            return 0;
        },
        upcomingActivitiesCount() {
            if (this.activeTab === 'upcoming' && this.paginatorInfo) {
                return this.paginatorInfo.total || 0;
            }
            return 0;
        },
        pastActivitiesCount() {
            if (this.activeTab === 'past' && this.paginatorInfo) {
                return this.paginatorInfo.total || 0;
            }
            return 0;
        },
        paginatorInfo() {
            return this.work_readiness_programs.paginatorInfo;
        },
        role() {
            return this.$store.getters.role;
        },
        userId() {
            return this.$store.getters.userId;
        },
        getActivityModalTitle() {
            return this.activityFormIndex !== null ? 'Edit Activity' : 'Add Activity';
        },
        getBadgeModalTitle() {
            return this.badgeFormIndex !== null ? 'Edit Badge' : 'Add Badge';
        },
    },
    apollo: {
        work_readiness_programs() {
            return {
                query: gql`query GetAllWorkReadinessProgram($first: Int!, $page: Int!, $filter: String) {
                    work_readiness_programs(first: $first, page: $page, filter: $filter) {
                        error
                        error_code
                        message
                        data {
                            token
                            title
                            description
                            start_date
                            end_date
                            points
                            valuation_type
                            is_external
                            external_link
                            created_at
                            creator {
                                id
                                name
                                profile_picture_url
                            }
                            submissions {
                                id
                                status
                            }
                        }
                        paginatorInfo {
                            count
                            currentPage
                            firstItem
                            hasMorePages
                            lastItem
                            lastPage
                            perPage
                            total
                        }
                    }
                }`,
                variables() {
                    return {
                        first: this.first,
                        page: this.page,
                        filter: this.activeTab === 'all' ? null : this.activeTab,
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
        badges: {
            query: GET_BADGES,
            loadingKey: 'badgesLoading',
            error(error) {
                this.badgeError = error.message || 'Failed to load badges';
            },
        },
        leaderboardIndex: {
            query: GET_LEADERBOARD,
            variables() {
                return {
                    page: this.leaderboardPageNumber,
                    first: this.leaderboardRecordPerPage,
                }
            },
            update(data) {
                this.leaderboard = data.leaderboardIndex.data
                this.leaderboardPaginatorInfo = data.leaderboardIndex.paginatorInfo
                return data
            },
            loadingKey: 'leaderboardLoading',
            error(error) {
                this.leaderboardError = error
            },
            fetchPolicy: 'network-only',
        }
    },
    methods: {
        setPage(page) {
            this.page = page;
        },
        setLeaderboardPage(page) {
            this.leaderboardPageNumber = page;
        },
        hasPermission(crudName) {
            if (this.role == 'superadmin') {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        isTheCreator(creatorUserId) {
            return this.role === 'superadmin' || creatorUserId === this.userId;
        },
        canEditActivity(activity) {
            if (!this.hasPermission('work-readiness-program-edit')) {
                return false;
            }
            if (!this.isCurrentActivity(activity) && !this.isUpcomingActivity(activity)) {
                return false;
            }
            const creatorId = activity && activity.creator ? activity.creator.id : null;
            return this.isTheCreator(creatorId);
        },
        openActivityModal(activity = null) {
            if (activity && !this.canEditActivity(activity)) {
                return;
            }
            this.isOpeningActivityModal = true;
            const defaultVal = (key) => {
                if (activity && activity[key] !== undefined && activity[key] !== null) return activity[key];
                if (key === 'points') return 0;
                if (key === 'is_external') return false;
                if (key === 'valuation_type') return activity && activity.is_external ? 'auto' : 'manual';
                if (key === 'start_date' || key === 'end_date') return null;
                if (key === 'external_link') return "";
                return "";
            };

            this.activityForm = ['title', 'description', 'points', 'valuation_type', 'is_external', 'external_link', 'start_date', 'end_date'].reduce((form, key) => {
                form[key] = {
                    val: defaultVal(key),
                    isValid: true,
                    msg: "",
                };
                return form;
            }, {});

            this.activityFormToken = activity ? activity.token : null;
            this.activityFormIndex = activity ? this.items.findIndex(item => item.token === activity.token) : null;
            this.showActivityModal = true;
            this.$nextTick(() => {
                this.isOpeningActivityModal = false;
            });
        },
        async saveActivity() {
            let isFormValid = true;

            for (const key in this.activityForm) {
                const field = this.activityForm[key];
                const value = field.val;

                // Skip validation for is_external (boolean) and valuation_type (always has default)
                if (key === 'is_external' || key === 'valuation_type') {
                    field.isValid = true;
                    field.msg = "";
                    continue;
                }

                // External link required only when is_external is true
                if (key === 'external_link') {
                    if (this.activityForm.is_external.val && (typeof value !== 'string' || value.trim() === '')) {
                        field.isValid = false;
                        field.msg = "External link is required for external registration.";
                        isFormValid = false;
                    } else {
                        field.isValid = true;
                        field.msg = "";
                    }
                    continue;
                }

                // Validation rules
                if (typeof value === 'string' && value.trim() === '') {
                    field.isValid = false;
                    field.msg = `${key.charAt(0).toUpperCase() + key.slice(1)} is required.`;
                    isFormValid = false;
                } else if (key === 'points' && (!Number.isInteger(value) || value < 1)) {
                    field.isValid = false;
                    field.msg = `Points must be an integer greater than or equal to 1.`;
                    isFormValid = false;
                } else {
                    field.isValid = true;
                    field.msg = "";
                }
            }

            if (!isFormValid) {
                return; // prevent submission
            }

            const formData = Object.fromEntries(
                Object.entries(this.activityForm)
                    .filter(([key]) => ['title', 'description', 'points', 'valuation_type', 'is_external', 'external_link', 'start_date', 'end_date'].includes(key))
                    .map(([key, field]) => [key, field.val])
            );
            if (!formData.is_external) {
                formData.external_link = null;
            }

            this.isLoading = true;
            let error_msg = null;
            let success_msg = null;
            const isEdit = this.activityFormToken !== null;

            if (isEdit) {
                const editingActivity = this.work_readiness_programs && this.work_readiness_programs.data
                    ? this.work_readiness_programs.data.find(function (item) {
                        return item.token === this.activityFormToken;
                    }.bind(this))
                    : null;
                if (editingActivity && !this.canEditActivity(editingActivity)) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Oops...",
                        text: "You can only edit activities you have created.",
                    });
                    this.isLoading = false;
                    return;
                }
            }

            try {
                const token = isEdit ? this.activityFormToken : null;

                const { data } = await this.$apollo.mutate({
                    mutation: isEdit ? EDIT_WORK_READINESS_PROGRAM : CREATE_WORK_READINESS_PROGRAM,
                    variables: {
                        token,
                        input: formData,
                    },
                });

                const response = isEdit
                    ? data.editWorkReadinessProgram
                    : data.createWorkReadinessProgram;

                if (response.error) {
                    error_msg = response.message;
                } else {
                    success_msg = response.message;
                }
            } catch (error) {
                error_msg = error.message || "Something went wrong. Please try again.";
            }

            if (error_msg === null) {
                this.$swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "success",
                    title: success_msg,
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });
            } else {
                this.$swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: error_msg,
                });
            }

            this.isLoading = false;
            this.resetActivityModal();
            isEdit || this.page == 1 ? this.$apollo.queries.work_readiness_programs.refetch() : this.page = 1;
        },
        async deleteActivity(token) {
            if (!token) return;

            const confirmed = await this.$swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
            });

            if (!confirmed.isConfirmed) return;

            this.isLoading = true;

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: DELETE_WORK_READINESS_PROGRAM,
                    variables: { token },
                });

                if (data.deleteWorkReadinessProgram.error) {
                    await this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.deleteWorkReadinessProgram.message,
                    });
                    return;
                }

                await this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: data.deleteWorkReadinessProgram.message,
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });

            } catch (error) {
                await this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not delete activity.',
                });
            } finally {
                this.isLoading = false;
                this.page == 1 ? this.$apollo.queries.work_readiness_programs.refetch() : this.page = 1;
            }
        },
        resetActivityModal() {
            this.showActivityModal = false;
            this.activityForm = {
                title: {
                    val: "",
                    isValid: true,
                    msg: "",
                },
                description: {
                    val: "",
                    isValid: true,
                    msg: "",
                },
                points: {
                    val: 0,
                    isValid: true,
                    msg: "",
                },
                valuation_type: {
                    val: "manual",
                    isValid: true,
                    msg: "",
                },
                is_external: {
                    val: false,
                    isValid: true,
                    msg: "",
                },
                external_link: {
                    val: "",
                    isValid: true,
                    msg: "",
                },
                start_date: {
                    val: null,
                    isValid: true,
                    msg: "",
                },
                end_date: {
                    val: null,
                    isValid: true,
                    msg: "",
                },
            };
            this.activityFormIndex = null;
            this.activityFormToken = null;
        },
        openBadgeModal(badge = null, index = null) {
            if (badge) {
                this.badgeForm = { ...badge };
                this.badgeFormIndex = index;
            } else {
                this.badgeForm = { name: '', level: 1, emoji: '🏅' };
                this.badgeFormIndex = null;
            }
            this.showBadgeModal = true;
        },
        saveBadge() {
            if (this.badgeFormIndex !== null) {
                this.badges[this.badgeFormIndex] = { ...this.badgeForm };
            } else {
                this.badges.push({ ...this.badgeForm });
            }
            this.resetBadgeModal();
        },
        deleteBadge(index) {
            this.badges.splice(index, 1);
        },
        resetBadgeModal() {
            this.showBadgeModal = false;
            this.badgeForm = { name: '', level: 1, emoji: '🏅' };
            this.badgeFormIndex = null;
        },
        goToActivity(token) {
            if (!token) return;
            this.$router.push(`/submitted-work-readiness-program/${token}`);
        },
        viewStudentProfile(studentId) {
            this.studentId = studentId;
            this.showStudentProfileFullModal = true;
        },
        closeFullModal() {
            this.showStudentProfileFullModal = false;
            this.studentId = null;
        },
        getProfilePicture(url) {
            if (!url) {
                return '/img/default-avatar.png';
            }
            if (url.indexOf('http') === 0) {
                return url;
            }
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        handleImageError(event) {
            event.target.src = '/img/default-avatar.png';
        },
        getTabLabel() {
            if (this.activeTab === 'all') {
                return 'activities';
            } else if (this.activeTab === 'current') {
                return 'current activities';
            } else if (this.activeTab === 'upcoming') {
                return 'upcoming activities';
            } else if (this.activeTab === 'past') {
                return 'past activities';
            }
            return 'activities';
        },
        switchTab(tab) {
            if (this.activeTab !== tab) {
                this.activeTab = tab;
                this.page = 1; // Reset to first page when switching tabs
                this.$apollo.queries.work_readiness_programs.refetch();
            }
        },
        isCurrentActivity(activity) {
            if (!activity.start_date) return false;
            const now = new Date();
            const startDate = new Date(activity.start_date);

            // Check if activity has started
            if (startDate > now) return false;

            // Check if activity has ended
            if (activity.end_date) {
                const endDate = new Date(activity.end_date);
                if (endDate < now) return false;
            }

            return true;
        },
        isUpcomingActivity(activity) {
            if (!activity.start_date) return false;
            const now = new Date();
            const startDate = new Date(activity.start_date);
            return startDate > now;
        },
        isPastActivity(activity) {
            if (!activity.end_date) return false;
            const now = new Date();
            const endDate = new Date(activity.end_date);
            return endDate < now;
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            const options = {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return date.toLocaleDateString('en-US', options);
        },
        getPendingSubmissions(activity) {
            if (!activity.submissions || !Array.isArray(activity.submissions)) {
                return 0;
            }
            return activity.submissions.filter(sub => {
                const status = sub.status ? sub.status.toLowerCase() : 'pending';
                return status === 'pending';
            }).length;
        },
        getApprovedSubmissions(activity) {
            if (!activity.submissions || !Array.isArray(activity.submissions)) {
                return 0;
            }
            return activity.submissions.filter(sub => {
                const status = sub.status ? sub.status.toLowerCase() : '';
                return status === 'approved' || status === 'completed';
            }).length;
        },
    },
};
</script>

<style scoped>
.truncate-2 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.line-clamp-3 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    line-clamp: 3;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.fade-student-enter-active,
.fade-student-leave-active {
    transition: opacity 0.3s ease;
}

.fade-student-enter-from,
.fade-student-leave-to {
    opacity: 0;
}

.mx-datepicker {
    width: 100% !important;
}

.activity-date-picker:deep(.mx-input-wrapper .mx-input) {
    border: 1px solid #d1d5db;
    box-shadow: none;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    height: 38px;
    border-radius: 0.5rem;
}

.activity-date-picker:deep(.mx-input-wrapper .mx-input:focus) {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.25);
}
</style>
